import * as React from 'react';
import { ReactElement, useState } from 'react';
import { Align, Position } from 'react-tiny-popover';
import { ISingleSelectListProps, SingleSelectList } from '../single-select-list/single-select-list';
import { CommonPopover } from '../common-popover/common-popover';
import { CommonPopoverContext } from '../common-popover/common-popover.context';
import { SearchInput } from '../search-input/search-input';
import { Component } from '../../../infrastructure/type-extensions';
import './make-single-select.scss';

/**
 * properties, required by 'popover' part of the single selection component
 */
interface ISingleSelectPopupProps {
    emptyListText?: string | ReactElement;
    emptyFilterText?: string | ReactElement;
    position?: Position;
    align?: Align;
}

/**
 * properties, passed to component which is supposed to trigger popover, derived from selection list and popover state
 * so component may change it's UI based on what happens inside the popover
 * note: properties might be expanded, for example we may wan't to pass selected value here
 */
interface ISingleSelectClosedPropsDerivedFromItemsList {
    name: string;
    isOpen: boolean;
    // eslint-disable-next-line @typescript-eslint/naming-convention
    ActionButton: () => ReactElement;
}

interface ISingleSelectButtonProps {
    className?: string;
    onClick?: () => void;
    containerClassName?: string;
}

/**
 * properties of component, used for triggering single selection in popover without ones,
 * passed from `makeSingleSelectInPopover` HOC
 */
type SingleSelectClosedOwnProps<T, > = Omit<T, keyof ISingleSelectClosedPropsDerivedFromItemsList>;

/**
 * remove all props, consumed by `makeSingleSelectInPopover` HOC and leave only those, which are consumed by concrete component,
 * supposed to trigger single select
 * @param props
 */
const extractPropsRemainderForClosedComponent = <TClosedComponentOwnProps, TItem, TKeyName extends keyof TItem & string>(
    props: ISingleSelectListProps<TItem, TKeyName>
        & ISingleSelectPopupProps
        & ISingleSelectButtonProps
        & SingleSelectClosedOwnProps<TClosedComponentOwnProps>
): SingleSelectClosedOwnProps<TClosedComponentOwnProps> => {
    const propsRemainder = { ...props };
    delete propsRemainder.keyProperty;
    delete propsRemainder.nameProperty;
    delete propsRemainder.value;
    delete propsRemainder.items;
    delete propsRemainder.onChange;
    delete propsRemainder.emptyListText;
    delete propsRemainder.emptyFilterText;
    delete propsRemainder.position;
    delete propsRemainder.align;
    delete propsRemainder.containerClassName;
    return propsRemainder;
};

/**
 * HOC which attach single-selection-list-inside-popover capabilities to `ComponentWhichTriggerSingleSelection`
 * IMPORTANT: ComponentWhichTriggerSingleSelection have to forward ref (this limitation comes from Popover usage)
 */
const makeSingleSelectInPopover = <T extends ISingleSelectClosedPropsDerivedFromItemsList>(ComponentWhichTriggerSingleSelection: Component<T>) => {
    // we need to utilize context.consumer in order to identify 'isOpen' value, but usage of popover forcing ref forwarding
    // therefore utilize React.forwardRef to create HOC over the ComponentWhichTriggerSingleSelection, which would utilize context and forward ref
    const ComponentWithForwardedRef = React.forwardRef<HTMLElement, T>((props: T, ref: React.Ref<HTMLElement>) => {
        return (
            <CommonPopoverContext.Consumer>
                {context => <ComponentWhichTriggerSingleSelection {...props} isOpen={context.isOpen} ref={ref} />}
            </CommonPopoverContext.Consumer>
        );
    });

    return <TItem, TKeyName extends keyof TItem & string>(
        props: ISingleSelectListProps<TItem, TKeyName>
            & ISingleSelectPopupProps
            & ISingleSelectButtonProps
            & SingleSelectClosedOwnProps<T>
            // eslint-disable-next-line @typescript-eslint/naming-convention
            & { ActionButton?: () => ReactElement }
    ) => {
        const [filterCriteria, setFilterCriteria] = useState<string>('');

        const selectedItem = props.items.find(x => x[props.keyProperty] === props.value);
        const name = selectedItem ? selectedItem[props.nameProperty] + '' : props.value;

        // filter all passed props through the search criteria entered in popover header
        const filteredItemsArray = filterCriteria
            ? props.items.filter(x => ('' + x[props.nameProperty]).toUpperCase().indexOf(filterCriteria.toUpperCase()) !== -1)
            : props.items;

        const body = (
            <CommonPopoverContext.Consumer>
                {context => {
                    // override clicking on option to ensure popover is closed once option is selected
                    const onOptionClick = (x: TItem[TKeyName]) => {
                        props.onChange(x);
                        context.closePopover();
                    };
                    return <>
                        {!!ActionButton && <div onClick={context.closePopover}><ActionButton /></div>}
                        {props.items.length === 0 && <div className='single-select-no-content'>{props.emptyListText || 'No items'}</div>}
                        {filteredItemsArray.length === 0 && props.items.length !== 0 &&
                        <div className='single-select-no-content'>{props.emptyFilterText || 'No items matching search criteria'}</div>}
                        <SingleSelectList
                            keyProperty={props.keyProperty}
                            nameProperty={props.nameProperty}
                            disabled={props.disabled}
                            value={props.value}
                            items={filteredItemsArray}
                            onChange={onOptionClick}
                        />
                    </>;
                }}
            </CommonPopoverContext.Consumer>
        );

        const targetComponentProps = {
            ...extractPropsRemainderForClosedComponent<T, TItem, TKeyName>(props),
            name: name,
        } as T;

        const { ActionButton } = props;

        // T can not be casted to ForwardRefExoticComponent<PropsWithoutRef<P> & RefAttributes<T>>
        // can't get the reason for this, but for now just case targetComponentProps to any while passing to component with forwarded ref
        /* eslint-disable @typescript-eslint/no-explicit-any */
        return (
            <CommonPopover
                header={<SearchInput onChange={e => setFilterCriteria(e.target.value)} className='w-100' value={filterCriteria} />}
                body={body}
                position={props.position || 'left'}
                align={props.align || 'start'}
                onClose={() => setFilterCriteria('')}
                containerClassName={props.containerClassName}
            >
                <ComponentWithForwardedRef {...targetComponentProps as any} />
            </CommonPopover>
        );
        /* eslint-enable @typescript-eslint/no-explicit-any */
    };
};

export { makeSingleSelectInPopover, ISingleSelectClosedPropsDerivedFromItemsList, ISingleSelectButtonProps };
